import { useMemo } from 'react';

import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import type { TextFieldProps } from '@mui/material/TextField';
import { Frequency } from 'rrule';

import useBuilderStore from '../../store/builderStore';

import type { MonthBy, YearlyBy } from './Repeat.types';
import RepeatDaily from './RepeatDaily/RepeatDaily';
import RepeatHourly from './RepeatHourly/RepeatHourly';
import RepeatMonthly from './RepeatMonthly/RepeatMonthly';
import RepeatWeekly from './RepeatWeekly/RepeatWeekly';
import RepeatYearly from './RepeatYearly/RepeatYearly';
import { frequencyTextMapping } from './utils';

interface RepeatSelectProps {
  rruleFrequencyOptions?: Frequency[];
  onFrequencyChange: (frequency: Frequency) => void;
  frequencySelected: Frequency;
  enableYearlyInterval: boolean;
  inputSize: 'small' | 'medium';
  inputVariant: TextFieldProps['variant'];
}

const defaultFrequencyOptions: Frequency[] = [
  Frequency.YEARLY,
  Frequency.MONTHLY,
  Frequency.WEEKLY,
  Frequency.DAILY,
  Frequency.HOURLY,
];

const RepeatSelect = ({
  rruleFrequencyOptions = defaultFrequencyOptions,
  frequencySelected,
  onFrequencyChange,
  enableYearlyInterval,
  inputSize = 'small',
  inputVariant,
}: RepeatSelectProps) => {
  const { setRepeatDetails, repeatDetails, radioValue, setRadioValue } = useBuilderStore();
  const menuItems = rruleFrequencyOptions.map((option) => (
    <MenuItem key={option} value={option}>
      {frequencyTextMapping[option]}
    </MenuItem>
  ));

  const repeatComponentToRender = useMemo(() => {
    // type narrow the repeatDetails

    switch (frequencySelected) {
      case Frequency.HOURLY:
        return (
          <RepeatHourly
            value={repeatDetails}
            onChange={setRepeatDetails}
            inputSize={inputSize}
            inputVariant={inputVariant}
          />
        );
      case Frequency.DAILY:
        return (
          <RepeatDaily
            value={repeatDetails}
            onChange={setRepeatDetails}
            inputSize={inputSize}
            inputVariant={inputVariant}
          />
        );
      case Frequency.WEEKLY:
        return (
          <RepeatWeekly
            value={repeatDetails}
            onChange={setRepeatDetails}
            inputSize={inputSize}
            inputVariant={inputVariant}
          />
        );
      case Frequency.MONTHLY:
        return (
          <RepeatMonthly
            value={repeatDetails}
            onChange={setRepeatDetails}
            radioValue={radioValue as MonthBy}
            setRadioValue={setRadioValue}
            inputSize={inputSize}
            inputVariant={inputVariant}
          />
        );
      case Frequency.YEARLY:
        return (
          <RepeatYearly
            value={repeatDetails}
            onChange={setRepeatDetails}
            enableYearlyInterval={enableYearlyInterval}
            radioValue={radioValue as YearlyBy}
            setRadioValue={setRadioValue}
            inputSize={inputSize}
            inputVariant={inputVariant}
          />
        );
      default:
        return null;
    }
  }, [
    enableYearlyInterval,
    frequencySelected,
    inputSize,
    inputVariant,
    radioValue,
    repeatDetails,
    setRadioValue,
    setRepeatDetails,
  ]);

  return (
    <Stack direction="column" spacing={2}>
      <Select
        value={frequencySelected}
        defaultValue={Frequency.WEEKLY}
        onChange={(e) => onFrequencyChange(e.target.value)}
        {...{ fullWidth: true, size: inputSize }}
        variant={inputVariant}
      >
        {menuItems}
      </Select>
      {repeatComponentToRender}
    </Stack>
  );
};

export default RepeatSelect;
